import { createRouter, createWebHistory } from "vue-router";
import store from "@/src/store/index.js";
import MixpanelService from "@/src/services/mixpanel/mixpanel.js";
import { getCleanPageName } from "@/src/utils/utils.js";
import { apiVariants } from "@/src/constants/appConstants";
import { PRODUCTS_SLUG, INVOICES_SLUG } from "@/src/constants/crmConstants.js";
import { standardObjectRouteNames } from "@/src/constants/objectConstants.js";
import {
  shouldRedirectToCrmRoute,
  getCrmRoutePath,
} from "@/src/utils/routeUtils.js";

const handleGetStartedRedirect = async (to, from, next) => {
  try {
    await store.dispatch("getStarted/fetchStatus");
    const isGetStartedComplete =
      store.getters["getStarted/isGetStartedComplete"];

    const appStoreVersion =
      window.APP_STORE_CONFIG?.version ||
      store.getters["appStore/appStoreVersion"];

    if (isGetStartedComplete) {
      const redirectTo = appStoreVersion === "v2" ? "i-assistant" : "home";
      next({ name: redirectTo });
    } else {
      const redirectTo =
        appStoreVersion === "v2" ? "i-assistant" : "get_started";
      next({ name: redirectTo });
    }
  } catch (error) {
    console.error(
      "An error occurred while handling get started redirect: ",
      error,
    );
  }
};

const routes = [
  {
    path: "/home",
    name: "home",
    component: () => import(`@/src/views/home/v1/Home.vue`),
    props: true,
  },
  {
    path: "/dashboards",
    name: "dashboards",
    component: () => import("@/src/views/dashboards/Dashboards.vue"),
    props: true,
  },
  {
    path: "/dashboards/templates",
    name: "dashboards-templates",
    component: () =>
      import("@/src/views/dashboards/templates/TemplatesView.vue"),
  },
  {
    path: "/dashboards_admin",
    name: "dashboards_admin",
    component: () => import("@/src/views/admin/AdminPanel.vue"),
    props: true,
  },
  {
    path: "/get_started",
    name: "get_started",
    component: () => import("@/src/views/get_started/GetStarted.vue"),
    props: true,
  },
  {
    path: "/",
    name: "root",
    beforeEnter: handleGetStartedRedirect,
  },
  {
    path: "/dashboard",
    name: "dashboard",
    beforeEnter: handleGetStartedRedirect,
  },
  {
    path: "/i-assistant",
    name: "i-assistant",
    component: () => import("@/src/views/chats/AgiChat.vue"),
  },
  {
    path: "/analytics",
    name: "analytics",
    component: () => import("@/src/views/analytics/v2/Analytics.vue"),
  },
  {
    path: "/ai-analytics",
    name: "analytics_v3",
    component: () => import("@/src/views/analytics/v3/dashboard/Analytics.vue"),
  },
  {
    path: "/dynamic/analytics",
    name: "dynamic-analytics",
    component: () => import("@/src/views/analytics/v3/dashboard/Analytics.vue"),
  },
  {
    path: "/analytics/dashboard/:dashboard_id",
    name: "analytics-dashboard",
    component: () =>
      import("@/src/views/analytics/v3/dashboard/AnalyticsDashboard.vue"),
    meta: { hideNavbar: true, hideSidebar: true },
  },
  {
    path: "/analytics/dashboard/templates",
    name: "analytics-templates",
    component: () =>
      import("@/src/views/analytics/v3/templates/TemplatesView.vue"),
  },
  {
    path: "/tasks",
    name: "tasks",
    component: () => import("@/src/views/dynamic_crm/DynamicCRMView.vue"),
    props: { slug: "crm_tasks" },
  },
  {
    path: "/refer",
    name: "refer",
    component: () => import("@/src/views/refer/Refer.vue"),
  },
  {
    path: "/settings/linkedin_account/:account_id",
    name: "linkedin-account-detail",
    component: () =>
      import("@/src/views/integrations/LinkedinIntegrationSettings.vue"),
    meta: { hideSidebar: true },
  },
  {
    path: "/conversations",
    name: "conversations",
    component: () => import("@/src/views/conversations/Conversations.vue"),
  },
  {
    path: "/unibox",
    name: "unibox",
    component: () => import("@/src/views/unibox/Unibox.vue"),
  },
  {
    path: "/emails",
    name: "emails",
    component: () => import("@/src/views/emails/Emails.vue"),
  },
  {
    path: "/linkedin/logs",
    name: "linkedin_logs",
    component: () => import("@/src/views/linkedin/LinkedinLogs.vue"),
  },
  {
    path: `/${apiVariants.OUTREACH}`,
    name: apiVariants.OUTREACH,
    component: () => import("@/src/views/campaign/Campaigns.vue"),
  },
  {
    path: "/ai-recruiter",
    name: "ai-recruiter",
    component: () => import("@/src/views/campaign/Campaigns.vue"),
  },
  {
    path: `/${apiVariants.SEQUENCES}`,
    name: apiVariants.SEQUENCES,
    component: () => import("@/src/views/sequence/Sequences.vue"),
  },
  {
    path: "/settings",
    name: "settings",
    component: () => import("@/src/views/settings/Settings.vue"),
    props: (route) => ({ tab: route.hash.slice(1) || "general" }),
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/conversation_intelligence/create",
    name: "conversation-intelligence-template-create",
    component: () =>
      import("@/src/views/calls/conversation_intelligence/ConversationIntelligenceTemplateCreateEdit.vue"),
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/conversation_intelligence/:id",
    name: "conversation-intelligence-template-edit",
    component: () =>
      import("@/src/views/calls/conversation_intelligence/ConversationIntelligenceTemplateCreateEdit.vue"),
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/custom_events/create",
    name: "custom-event-create",
    component: () => import("@/src/views/custom_events/CreateCustomEvent.vue"),
    meta: { hideSidebar: true },
  },
  {
    path: "/search",
    name: "search",
    component: () => import("@/src/views/search/Search.vue"),
  },
  {
    path: "/market",
    name: "market",
    component: () => import("@/src/views/app_store/AppStore.vue"),
    meta: { hideSidebar: true, public: true },
  },
  {
    path: "/market/:slug+",
    name: "market-detail",
    component: () => import("@/src/views/app_store/AppDetail.vue"),
    props: (route) => ({
      slug: Array.isArray(route.params.slug)
        ? route.params.slug.join("/")
        : route.params.slug,
    }),
    meta: { hideSidebar: true, public: true },
  },
  {
    path: "/crm/:slug",
    name: "dynamic-crm",
    component: () => import("@/src/views/dynamic_crm/DynamicCRMView.vue"),
    // Same rationale as `/crm/:slug/:id`: CRM-scoped list route defaults
    // service to 'crm' so shared slugs don't hard-pin to the wrong service.
    props: (route) => ({
      slug: route.params.slug,
      service: route.query.service || "crm",
    }),
  },
  {
    path: "/layout-editor",
    name: "layout-editor",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/LayoutEditorPage.vue"),
    props: (route) => ({
      slug: route.query.slug,
      entityId: route.query.entityId,
      projectId: route.query.projectId,
      service: route.query.service,
    }),
    meta: { hideSidebar: true, hideNavbar: true },
  },
  {
    path: "/crm/:slug/:id",
    name: "dynamic-crm-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    // `/crm/:slug/:id` is the CRM-scoped detail route; system-service slugs
    // (products, invoices, line_items) have their own routes. Default the
    // service to 'crm' so a user-created `products` in service=crm doesn't
    // fall back to SYSTEM_SLUG_SERVICE['products'] and hit the wrong service.
    // Route query still wins when explicitly set.
    props: (route) => ({
      slug: route.params.slug,
      id: route.params.id,
      service: route.query.service || "crm",
    }),
  },
  {
    path: "/companies/:id",
    name: "company-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({
      id: route.params.id,
      slug: "companies",
      source: route.query.source || null,
    }),
  },
  {
    path: "/prospect_companies/:id",
    name: "prospect-company-detail",
    component: () => import("@/src/views/prospect/ProspectCompanyView.vue"),
    props: true,
  },
  {
    path: "/prospect/find_companies",
    name: "find-companies",
    component: () => import("@/src/views/prospect/Prospect.vue"),
    props: true,
  },
  {
    path: "/campaigns/:id/edit",
    name: "campaign-edit",
    component: () => import("@/src/views/campaign/CampaignMainEdit.vue"),
    props: true,
  },
  {
    path: "/sequences/templates",
    name: "sequence_templates",
    component: () =>
      import("@/src/views/sequence/templates/SequenceTemplates.vue"),
    props: true,
  },
  {
    path: "/sequences/templates_admin",
    name: "/sequences/sequence_templates_admin",
    component: () =>
      import("@/src/views/sequence/templates/SequenceTemplatesAdmin.vue"),
    props: true,
  },
  {
    path: "/sequences/:id",
    name: "sequence-edit",
    component: () => import("@/src/views/sequence/actions/SequenceEdit.vue"),
    props: true,
  },
  {
    path: "/sequences/:id/launch",
    name: "sequence-launch",
    component: () => import("@/src/views/sequence/actions/SequenceLaunch.vue"),
    props: true,
  },
  {
    path: "/sequences/:id/step/:variant_id/edit",
    name: "sequence-step-edit",
    component: () =>
      import("@/src/views/sequence/details/SequenceStepDetail.vue"),
    props: true,
  },
  {
    path: "/campaigns/:id",
    name: "campaign-detail",
    component: () => import("@/src/views/campaign/CampaignDetail.vue"),
    props: true,
  },
  {
    path: "/deals/:id",
    name: "deals-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({
      id: route.params.id,
      slug: "deals",
    }),
  },
  {
    path: "/tasks/:id",
    name: "tasks-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({
      slug: "crm_tasks",
      id: route.params.id,
    }),
  },
  {
    path: "/crm/tasks/:id",
    name: "crm-tasks-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({
      slug: "crm_tasks",
      id: route.params.id,
    }),
  },
  {
    path: "/contacts/:id",
    name: "contact-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({
      id: route.params.id,
      slug: "contacts",
    }),
  },
  {
    path: "/entity-invalid",
    name: "entity-invalid",
    component: () => import("@/src/views/contacts/EntityNotFound.vue"),
    props: true,
  },
  {
    path: "/prospect_leads/:id",
    name: "prospect-lead-detail",
    component: () => import("@/src/views/prospect/ProspectLeadView.vue"),
    props: true,
  },
  {
    path: "/prospect/find_leads",
    name: "find-leads",
    component: () => import("@/src/views/prospect/Prospect.vue"),
    props: true,
  },
  {
    path: "/leads/:id",
    name: "lead-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({
      id: route.params.id,
      slug: "leads",
      source: route.query.source || null,
    }),
  },
  {
    path: "/settings/integrations/:name",
    name: "integration-details",
    component: () => import("@/src/views/integrations/IntegrationDetail.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/integrations/whatsapp",
    name: "whatsapp-integration-details",
    component: () => import("@/src/views/integrations/whatsapp/WhatsApp.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/integrations/whatsapp/connect",
    name: "whatsapp-connect-account",
    component: () =>
      import("@/src/views/integrations/whatsapp/ConnectAccount.vue"),
    props: true,
    meta: { hideSidebar: true, hideNavbar: true },
  },
  {
    path: "/settings/integrations/whatsapp/templates_table/:config_id?",
    name: "whatsapp-templates-table",
    component: () =>
      import("@/src/views/integrations/whatsapp/WhatsappTempleteTable.vue"),
    props: true,
    meta: { hideSidebar: true, hideNavbar: false },
  },
  {
    path: "/settings/integrations/whatsapp/:config_id/create_template",
    name: "whatsapp-create-template",
    component: () =>
      import("@/src/views/integrations/whatsapp/CreateTemplete.vue"),
    props: true,
    meta: { hideSidebar: true, hideNavbar: false },
  },
  {
    path: "/settings/integrations/whatsapp/:config_id/edit_template/:template_id",
    name: "whatsapp-edit-template",
    component: () =>
      import("@/src/views/integrations/whatsapp/CreateTemplete.vue"),
    props: true,
    meta: { hideSidebar: true, hideNavbar: false },
  },
  {
    path: "/settings/permissions/:role_id",
    name: "permission-management",
    component: () => import("@/src/views/users/PermissionManagement.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/integrations/:name/manage",
    name: "manage-integration",
    component: () => import("@/src/views/integrations/ManageIntegrations.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/business/:country/manage",
    name: "verify-business",
    component: () => import("@/src/views/business/VerifyBusiness.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/entity_scoring/:id",
    name: "entity-scoring",
    component: () => import("@/src/views/entity_scoring/EditEntityScoring.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/objects/create",
    name: "create-custom-object",
    component: () => import("@/src/views/objects/CreateCustomObject.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/objects/customize/:slug",
    name: "customize-object",
    component: () => import("@/src/views/objects/CustomizeObject.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/objects/edit/:slug",
    name: "edit-custom-object",
    component: () => import("@/src/views/objects/CreateCustomObject.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/objects/:slug/customize/create-form",
    name: "customize-create-form",
    component: () => import("@/src/views/objects/CustomizeCreateForm.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/:slug/customize/associations/:targetSlug",
    name: "create-association",
    component: () =>
      import("@/src/views/objects/associations/CreateAssociation.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/objects/:entityType/properties",
    name: "entity-properties",
    component: () => import("@/src/views/objects/EntityProperties.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/objects/:entityType/properties",
    name: "entity-properties-name",
    children: [
      {
        path: "static/:propertyName",
        component: () => import("@/src/views/objects/ObjectManageProperty.vue"),
        props: true,
        meta: { hideSidebar: true },
      },
      {
        path: ":propertyName",
        component: () =>
          import("@/src/views/objects/EntityPropertyDetails.vue"),
        props: true,
        meta: { hideSidebar: true },
      },
    ],
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/objects/customers",
    name: "customers-properties",
    component: () => import("@/src/views/objects/CustomerPropertyDetails.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/create_route",
    name: "create-route",
    component: () =>
      import("@/src/views/calls/call_routing/CreateOrUpdateCallRoute.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/update_route/:id",
    name: "update-route",
    component: () =>
      import("@/src/views/calls/call_routing/CreateOrUpdateCallRoute.vue"),
    props: (route) => ({
      source: "call_routing",
      id: route.params.id,
    }),
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/mailboxes/:id",
    name: "mailbox-signature",
    component: () =>
      import("@/src/views/integrations/EmailIntegrationSettings.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/settings/mailboxes/generate",
    name: "generate-mailboxes",
    component: () => import("@/src/views/mailboxes/GenerateMailboxes.vue"),
    meta: { hideSidebar: true, hideNavbar: true },
  },
  {
    path: "/content",
    name: "content",
    component: () => import("@/src/views/content/Content.vue"),
    props: true,
  },
  {
    path: "/content/:id",
    name: "content-detail",
    component: () => import("@/src/views/content/ContentView.vue"),
    props: true,
  },
  {
    path: "/onboarding",
    name: "onboarding",
    component: () => import("@/src/views/onboarding/Onboarding.vue"),
    meta: { hideSidebar: true, hideNavbar: false },
  },
  {
    path: "/prospect",
    name: "prospect",
    component: () => import("@/src/views/prospect/Prospect.vue"),
  },
  {
    path: "/data_enrichments",
    name: "data_enrichments",
    component: () => import("@/src/views/data_enrichment/DataEnrichment.vue"),
  },
  {
    path: "/list",
    name: "entity-list",
    component: () => import("@/src/views/entity_lists/EntityLists.vue"),
    props: true,
  },
  {
    path: "/list/:id/:entity_type",
    name: "entity-list-detail",
    component: () => import("@/src/views/entity_lists/EntityListView.vue"),
    props: true,
  },
  {
    path: "/meeting_agent",
    name: "meeting_agent",
    component: () =>
      import("@/src/views/meetings/meeting_agent/MeetingAgent.vue"),
    props: true,
  },
  {
    path: "/meeting_log",
    name: "meeting_log",
    component: () => import("@/src/views/meetings/meeting_log/MeetingLogs.vue"),
    props: true,
  },
  {
    path: "/ai_notetaker",
    name: "ai_notetaker",
    component: () =>
      import("@/src/views/meetings/ai_notetaker/AINotetaker.vue"),
    props: true,
  },
  {
    path: "/meeting_log/:id",
    name: "meeting_log_detail",
    component: () =>
      import("@/src/views/meetings/meeting_log/MeetingLogView.vue"),
    props: true,
  },
  {
    path: "/routing_form",
    name: "routing_form",
    component: () => import("@/src/views/routing_form/RoutingForm.vue"),
    props: true,
  },
  {
    path: "/routing_form/:id",
    name: "routing_form_create",
    component: () => import("@/src/views/routing_form/CreateRoutingForm.vue"),
    props: true,
  },
  {
    path: "/business-phone/call_logs",
    name: "business-phone-call-logs",
    component: () => import("@/src/views/calls/call_logs/CallLogs.vue"),
    props: true,
  },
  {
    path: "/business-phone/call_logs/:id",
    name: "business-phone-call-log-detail",
    component: () =>
      import("@/src/views/calls/call_logs/CallLogDetailView.vue"),
    props: true,
  },
  {
    path: "/sales-dialer/campaigns",
    name: "sales-dialer-campaigns",
    component: () =>
      import("@/src/views/calls/call_campaigns/CallCampaigns.vue"),
    props: true,
  },
  {
    path: "/sales-dialer/campaign/:id",
    name: "sales-dialer-campaign-detail",
    component: () =>
      import("@/src/views/calls/call_campaigns/CallCampaignView.vue"),
    props: true,
  },
  {
    path: "/sales-dialer/campaign/create/:campaignName?",
    name: "sales-dialer-campaign-create",
    component: () =>
      import("@/src/views/calls/call_campaigns/CreateCallCampaign.vue"),
    props: (route) => ({
      entityListId: Number(route.params.entityListId) || null,
    }),
  },
  {
    path: "/sales-dialer/call_logs",
    name: "sales-dialer-call-logs",
    component: () => import("@/src/views/calls/call_logs/CallLogs.vue"),
    props: true,
  },
  {
    path: "/sales-dialer/call_logs/:id",
    name: "sales-dialer-call-log-detail",
    component: () =>
      import("@/src/views/calls/call_logs/CallLogDetailView.vue"),
    props: true,
  },
  {
    path: "/signals",
    name: "signals",
    component: () => import("@/src/views/signals/Signals.vue"),
    props: true,
  },
  {
    path: "/signals/:id",
    name: "signals-setup",
    component: () => import("@/src/views/signals/SignalSetup.vue"),
    props: true,
  },
  {
    path: `/${apiVariants.WORKFLOWS}`,
    name: apiVariants.WORKFLOWS,
    component: () => import("@/src/views/automations/Automations.vue"),
    props: true,
  },
  {
    path: `/${apiVariants.WORKFLOWS}/:id`,
    name: `${apiVariants.WORKFLOWS}-editor`,
    component: () => import("@/src/views/automations/AutomationEditor.vue"),
    meta: { hideSidebar: true, hideNavbar: true },
  },
  {
    path: `/${apiVariants.CODER}`,
    name: apiVariants.CODER,
    component: () => import("@/src/views/coder/Coder.vue"),
  },
  {
    path: `/${apiVariants.CODER}/editor/:appId`,
    name: `${apiVariants.CODER}-editor`,
    component: () => import("@/src/views/coder/CoderEditor.vue"),
  },
  {
    path: `/${apiVariants.CODER}/:templateSlug`,
    name: `${apiVariants.CODER}-template`,
    component: () => import("@/src/views/coder/CoderTemplate.vue"),
  },
  {
    path: `/${apiVariants.CODER}/:templateSlug/editor/:appId`,
    name: `${apiVariants.CODER}-template-editor`,
    component: () => import("@/src/views/coder/CoderTemplateEditor.vue"),
  },
  {
    path: `/${apiVariants.PROCESS_FLOWS}`,
    name: apiVariants.PROCESS_FLOWS,
    component: () => import("@/src/views/process_flows/ProcessFlows.vue"),
    props: true,
  },
  {
    path: `/${apiVariants.PROCESS_FLOWS}/:id`,
    name: `${apiVariants.PROCESS_FLOWS}_editor`,
    component: () => import("@/src/views/process_flows/ProcessFlowEditor.vue"),
    meta: { hideSidebar: true, hideNavbar: true },
  },
  {
    path: "/generate-domains",
    name: "generate-domains",
    component: () => import("@/src/views/domains/AddDomain.vue"),
    meta: { hideSidebar: true, hideNavbar: true },
  },
  {
    path: "/domains",
    name: "domains",
    component: () => import("@/src/views/mailboxes/Domains.vue"),
    meta: { hideSidebar: false, hideNavbar: false },
  },
  {
    path: "/domains/:id/dns",
    name: "domain-dns",
    component: () => import("@/src/views/mailboxes/DomainDns.vue"),
    props: (route) => ({
      id: route.params.id,
      domain: route.query.domain || "Domain",
    }),
    meta: { hideSidebar: true, hideNavbar: false },
  },
  {
    path: "/setup_billing",
    name: "setup-billing",
    component: () => import("@/src/views/billing/setup/SetupBillingV2.vue"),
    meta: { hideSidebar: true, hideNavbar: false },
  },
  {
    path: "/marketing/campaigns",
    name: "campaigns_in_engage",
    component: () => import("@/src/views/campaigns_in_engage/Campaigns.vue"),
    props: true,
  },
  {
    path: "/marketing/campaigns/channel-selection",
    name: "campaign_channel_selection",
    component: () =>
      import("@/src/views/campaigns_in_engage/CampaignChannelSelection.vue"),
    props: true,
  },
  {
    path: "/marketing/campaigns/create/email-campaign",
    name: "create_email_campaign",
    component: () =>
      import("@/src/views/campaigns_in_engage/CreateEmailCampaign.vue"),
    props: true,
  },
  // {
  //   path: "/marketing/campaigns/create/whatsapp-campaign",
  //   name: "create_whatsapp_campaign",
  //   component: () =>
  //     import("@/src/views/campaigns_in_engage/CreateWhatsAppCampaign.vue"),
  //   props: true,
  // },
  {
    path: "/marketing/campaigns/create/whatsapp-campaign",
    name: "create_whatsapp_campaign_v2",
    component: () =>
      import("@/src/views/campaigns_in_engage/CreateWhatsAppCampaignRefactored.vue"),
    props: true,
  },
  {
    path: "/marketing/campaigns/create/whatsapp-campaign/:id",
    name: "edit_whatsapp_campaign_v2",
    component: () =>
      import("@/src/views/campaigns_in_engage/CreateWhatsAppCampaignRefactored.vue"),
    props: true,
  },
  {
    path: "/email-templates",
    name: "email-templates",
    component: () =>
      import("@/src/views/campaigns_in_engage/EmailTemplatesView.vue"),
    props: true,
  },
  {
    path: "/marketing/campaigns/email-campaign/details/:id",
    name: "campaign_email_detail",
    component: () =>
      import("@/src/views/campaigns_in_engage/CampaignDetails.vue"),
    props: true,
  },
  {
    path: "/marketing/campaigns/whatsapp-campaign/details/:id",
    name: "campaign_whatsapp_detail",
    component: () =>
      import("@/src/views/campaigns_in_engage/CampaignDetails.vue"),
    props: true,
  },
  {
    path: "/marketing/campaigns/create/email-campaign/plain-text-editor",
    name: "plain_text_editor",
    component: () =>
      import("@/src/views/campaigns_in_engage/PlainTextEditorComponent.vue"),
    props: true,
  },
  {
    path: "/workflows/marketing/campaigns/create/email-campaign/plain-text-editor",
    name: "workflows_plain_text_editor",
    component: () =>
      import("@/src/views/campaigns_in_engage/PlainTextEditorComponent.vue"),
    props: true,
    meta: { hideSidebar: true, hideNavbar: true },
  },
  {
    path: "/marketing/campaigns/create/email-campaign/html-editor",
    name: "html_editor",
    component: () =>
      import("@/src/views/campaigns_in_engage/HtmlEditorComponent.vue"),
    props: true,
  },
  {
    path: "/workflows/marketing/campaigns/create/email-campaign/html-editor",
    name: "workflow_html_editor",
    component: () =>
      import("@/src/views/campaigns_in_engage/HtmlEditorComponent.vue"),
    props: true,
    meta: { hideSidebar: true, hideNavbar: true },
  },
  {
    path: "/marketing/campaigns/create/email-campaign/drag-drop-editor",
    name: "drag_drop_editor",
    component: () =>
      import("@/src/views/campaigns_in_engage/DragNDropEditorComponent.vue"),
    props: true,
  },
  {
    path: "/workflows/marketing/campaigns/create/email-campaign/drag-drop-editor",
    name: "workflow_drag_drop_editor",
    component: () =>
      import("@/src/views/campaigns_in_engage/DragNDropEditorComponent.vue"),
    props: true,
    meta: { hideSidebar: true, hideNavbar: true },
  },
  {
    path: "/marketing/campaigns/create/email-campaign/drag-drop-editor/unlayer-editor",
    name: "unlayer_editor",
    component: () =>
      import("@/src/views/campaigns_in_engage/EmailUnlayerEditor.vue"),
    props: true,
    meta: { hideSidebar: true, hideNavbar: true },
  },
  {
    path: "/settings/email_campaign_settings/sender-domains/update",
    name: "sender-domains",
    component: () =>
      import("@/src/components/email_campaign_settigns/CreateSenderDomain.vue"),
    props: true,
    meta: { hideSidebar: true },
  },
  {
    path: "/inbound_agent/inbox",
    name: "inbound_agent_inbox",
    component: () => import("@/src/views/inbound_agent/inbox/Inbox.vue"),
  },
  {
    path: "/inbound_agent/instructions",
    name: "inbound_agent_instructions",
    component: () =>
      import("@/src/views/inbound_agent/instructions/Instructions.vue"),
  },
  {
    path: "/inbound_agent/knowledge_base",
    name: "inbound_agent_knowledge_base",
    component: () =>
      import("@/src/views/inbound_agent/knowledge_base/KnowledgeBase.vue"),
  },
  {
    path: "/inbound_agent/live_chat",
    name: "live_chat",
    component: () =>
      import("@/src/views/inbound_agent/live_chat/LiveChatWidget.vue"),
  },
  {
    path: "/tickets",
    name: "tickets",
    component: () => import("@/src/views/inbound_agent/tickets/Tickets.vue"),
  },
  {
    path: "/tickets/:id",
    name: "ticket-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({ slug: "tickets", id: route.params.id }),
  },
  {
    path: "/approvals",
    name: "approvals",
    component: () => import("@/src/views/approvals/Approvals.vue"),
  },
  {
    path: "/approvals/:id",
    name: "approval-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({ slug: "approval_requests", id: route.params.id }),
  },
  {
    path: "/spaces/projects/tasks",
    name: "project-tasks",
    component: () => import("@/src/views/projects/tasks/TasksPage.vue"),
  },
  {
    path: "/spaces/projects/:projectId/tasks/:id",
    name: "project-task-detail",
    component: () => import("@/src/views/projects/tasks/TaskDetailPage.vue"),
    props: (route) => ({
      slug: "tasks",
      id: route.params.id,
      projectId: route.params.projectId,
    }),
  },
  {
    path: "/projects/chats",
    name: "chats",
    component: () => import("@/src/views/projects/chats/Chats.vue"),
  },
  {
    path: "/external/join/:token/select-workspace",
    name: "external-group-select-workspace",
    component: () => import("@/src/views/external/ExternalGroupJoin.vue"),
    meta: { hideNavbar: true, hideSidebar: true },
  },
  {
    path: "/projects/notes",
    name: "notes",
    component: () => import("@/src/views/projects/notes/Notes.vue"),
  },
  {
    path: "/projects/notes/:noteSlugId",
    name: "note-detail",
    component: () => import("@/src/views/projects/notes/Notes.vue"),
  },
  {
    path: "/developer_tools/ai-qa",
    name: "qa",
    component: () => import("@/src/views/qa/QAPage.vue"),
  },
  {
    path: "/developer_tools/ai-qa/suites/:suiteId",
    name: "qa-suite-content",
    component: () => import("@/src/views/qa/QAPage.vue"),
    props: true,
  },
  {
    path: "/developer_tools/ai-qa/tests/:id",
    name: "qa-test-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({ slug: "qa_tests", id: route.params.id }),
  },
  {
    path: "/developer_tools/ai-qa/test-runs/:id",
    name: "qa-test-run-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({ slug: "qa_test_runs", id: route.params.id }),
  },
  {
    path: "/developer_tools/ai-qa/test-steps/:id",
    name: "qa-test-step-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({ slug: "qa_test_steps", id: route.params.id }),
  },
  {
    path: "/developer_tools/ai-qa/issues/:id",
    name: "qa-issue-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({ slug: "qa_issues", id: route.params.id }),
  },
  // Products
  {
    path: "/products",
    name: "products",
    component: () => import("@/src/views/dynamic_crm/DynamicCRMView.vue"),
    props: () => ({ slug: PRODUCTS_SLUG }),
  },
  {
    path: "/products/:id",
    name: "product-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({ slug: PRODUCTS_SLUG, id: route.params.id }),
  },
  // Invoices
  {
    path: "/invoices",
    name: "invoices",
    component: () => import("@/src/views/dynamic_crm/DynamicCRMView.vue"),
    props: () => ({ slug: INVOICES_SLUG }),
  },
  {
    path: "/invoices/line-items/:id",
    name: "invoice-line-item-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({ slug: "line_items", id: route.params.id }),
  },
  {
    path: "/invoices/:id",
    name: "invoice-detail",
    component: () =>
      import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
    props: (route) => ({ slug: INVOICES_SLUG, id: route.params.id }),
  },
  {
    path: "/developer_tools/error-logging",
    name: "error-logging",
    component: () => import("@/src/views/error-tracking/ErrorTrackingPage.vue"),
  },
  {
    path: "/developer_tools/error-logging/services/:serviceId",
    name: "error-logging-service",
    component: () => import("@/src/views/error-tracking/ErrorTrackingPage.vue"),
    props: true,
  },
  {
    path: "/developer_tools/error-logging/issues/:issueId",
    name: "error-logging-issue-detail",
    component: () =>
      import("@/src/views/error-tracking/ErrorTrackingIssueDetailPage.vue"),
    props: true,
  },
  {
    path: "/settings/error-logging/alerts/new",
    name: "error-logging-alert-create",
    component: () =>
      import("@/src/views/error-tracking/AlertIntegrationDetailPage.vue"),
    meta: { hideSidebar: true, hideNavbar: false },
  },
  {
    path: "/settings/error-logging/alerts/:alertId",
    name: "error-logging-alert-detail",
    component: () =>
      import("@/src/views/error-tracking/AlertIntegrationDetailPage.vue"),
    props: true,
    meta: { hideSidebar: true, hideNavbar: false },
  },
  {
    path: "/website_visitors",
    name: "website-visitors",
    component: () => import("@/src/views/website_visitors/WebsiteVisitors.vue"),
  },
  {
    path: "/customer_success/summary",
    name: "customer-success-summary",
    component: () => import("@/src/views/customer_success/Summary.vue"),
  },
  {
    path: "/customer_success/onboarding",
    name: "customer-success-onboarding",
    component: () => import("@/src/views/customer_success/Onboarding.vue"),
  },
  {
    path: "/customer_success/customers",
    name: "customer-success-customers",
    component: () => import("@/src/views/customer_success/Customers.vue"),
  },
  {
    path: "/customer_success/deals",
    name: "customer-success-deals",
    component: () => import("@/src/views/customer_success/Deals.vue"),
  },
  {
    path: "/customer_success/tasks",
    name: "customer-success-tasks",
    component: () => import("@/src/views/customer_success/Tasks.vue"),
  },
  {
    path: "/customer_success/alerts",
    name: "customer-success-alerts",
    component: () => import("@/src/views/customer_success/Alerts.vue"),
  },
  {
    path: "/website_visitors/setup",
    name: "website-visitors-setup",
    component: () =>
      import("@/src/views/website_visitors/WebsiteVisitorsSetup.vue"),
  },
  {
    path: "/dashboards/editor/:id",
    name: "dashboard-editor",
    component: () =>
      import("@/src/views/dashboards/editor/DashboardEditor.vue"),
  },
  {
    path: "/voice-agents/agents",
    name: "voice-agents",
    component: () => import("@/src/views/voice_agents/Agents.vue"),
    props: true,
  },
  {
    path: "/voice-agents/agents/templates",
    name: "voice-agents-templates",
    component: () => import("@/src/views/voice_agents/TemplateSelection.vue"),
    props: true,
  },
  {
    path: "/voice-agents/agents/create",
    name: "voice-agents-create",
    component: () => import("@/src/views/voice_agents/CreateOrEditAgent.vue"),
    props: true,
  },
  {
    path: "/voice-agents/agents/:id/edit",
    name: "voice-agents-edit",
    component: () => import("@/src/views/voice_agents/CreateOrEditAgent.vue"),
    props: true,
  },
  {
    path: "/voice-agents/campaign",
    name: "voice-agents-campaign",
    component: () => import("@/src/views/voice_agents/VoiceAgentCampaign.vue"),
    props: true,
  },
  {
    path: "/voice-agents/campaign/create",
    name: "voice-agents-campaign-create",
    component: () =>
      import("@/src/views/voice_agents/CreateVoiceAgentCampaign.vue"),
    props: true,
  },
  {
    path: "/voice-agents/campaign/:id/edit",
    name: "voice-agents-campaign-edit",
    component: () =>
      import("@/src/views/voice_agents/CreateOrEditVoiceAgentCampaign.vue"),
    props: true,
  },
  {
    path: "/voice-agents/campaign/:id",
    name: "voice-agents-campaign-view",
    component: () =>
      import("@/src/views/voice_agents/VoiceAgentCampaignView.vue"),
  },
  {
    path: "/voice-agents/call-logs",
    name: "voice-agents-call-logs",
    component: () => import("@/src/views/voice_agents/VoiceAgentCallLogs.vue"),
    props: true,
  },
  {
    path: "/voice-agents/call-logs/:id",
    name: "voice-agents-call-log-detail",
    component: () =>
      import("@/src/views/voice_agents/VoiceAgentCallLogDetail.vue"),
    props: true,
  },
  {
    path: "/business-receptionist",
    name: "business-receptionist",
    component: () =>
      import("@/src/views/business_receptionist/BusinessReceptionist.vue"),
    props: true,
  },
  {
    path: "/business-receptionist/create",
    name: "business-receptionist-create",
    component: () =>
      import("@/src/views/business_receptionist/CreateOrEditReceptionist.vue"),
    props: true,
  },
  {
    path: "/business-receptionist/:id/edit",
    name: "business-receptionist-edit",
    component: () =>
      import("@/src/views/business_receptionist/CreateOrEditReceptionist.vue"),
    props: true,
  },
  {
    path: "/business-receptionist/detail/:id",
    name: "business-receptionist-detail",
    component: () =>
      import("@/src/views/business_receptionist/BusinessReceptionistDetail.vue"),
    props: true,
  },
  {
    path: "/forms",
    name: "forms",
    component: () => import("@/src/views/forms/pages/Forms.vue"),
    props: true,
  },
  {
    path: "/forms/:id/edit",
    name: "form-edit",
    component: () => import("@/src/views/forms/editor/FormsEdit.vue"),
    props: true,
    meta: { hideNavbar: true, hideSidebar: true },
  },
  {
    path: "/forms/:id",
    name: "form-detail",
    component: () => import("@/src/views/forms/pages/FormsDetail.vue"),
    props: true,
  },
  // ── Digital Employee (disabled) ──────────────────────────────────────
  // {
  //   path: "/digital-employees",
  //   component: () =>
  //     import("@/src/views/digital-employee/DigitalEmployeeLayout.vue"),
  //   children: [
  //     {
  //       path: "",
  //       name: "digital-employees-listing",
  //       component: () => import("@/src/views/digital-employee/ListingPage.vue"),
  //     },
  //     {
  //       path: "select-type",
  //       name: "digital-employees-select-type",
  //       component: () =>
  //         import("@/src/views/digital-employee/TypeSelectionPage.vue"),
  //     },
  //     {
  //       path: "create",
  //       name: "digital-employees-create",
  //       component: () => import("@/src/views/digital-employee/CreatePage.vue"),
  //     },
  //     {
  //       path: ":id",
  //       name: "digital-employees-detail",
  //       component: () => import("@/src/views/digital-employee/DetailPage.vue"),
  //       props: true,
  //     },
  //     {
  //       path: ":id/session/:sessionId",
  //       name: "digital-employees-session",
  //       component: () =>
  //         import("@/src/views/digital-employee/SessionDetailPage.vue"),
  //       props: true,
  //     },
  //   ],
  // },
  // ── Inference Router ──────────────────────────────────────────────
  {
    path: "/inference-router",
    component: () =>
      import("@/src/views/inference-router/InferenceRouterLayout.vue"),
    children: [
      {
        path: "",
        redirect: "/inference-router/model-providers",
      },
      {
        path: "model-providers",
        name: "inference-router-providers",
        component: () =>
          import("@/src/views/inference-router/ModelProvidersPage.vue"),
      },
      {
        path: "model-providers/:id",
        name: "inference-router-provider-detail",
        component: () =>
          import("@/src/views/inference-router/ProviderDetailPage.vue"),
        props: true,
      },
      {
        path: "routing-logic",
        name: "inference-router-routing-logic",
        component: () =>
          import("@/src/views/inference-router/RoutingLogicPage.vue"),
      },
      {
        path: "analytics",
        name: "inference-router-analytics",
        component: () =>
          import("@/src/views/inference-router/AnalyticsPage.vue"),
      },
      {
        path: "logs",
        name: "inference-router-logs",
        component: () => import("@/src/views/inference-router/LogsPage.vue"),
      },
      {
        path: "setup",
        name: "inference-router-setup",
        component: () => import("@/src/views/inference-router/SetupPage.vue"),
      },
    ],
  },
  {
    path: "/live-assist/download",
    name: "live-assist-download",
    component: () => import("@/src/views/live_assist/DownloadApp.vue"),
    props: true,
  },
  {
    path: "/live-assist/templates",
    name: "live-assist-templates",
    component: () => import("@/src/views/live_assist/Templates.vue"),
    props: true,
  },
  {
    path: "/live-assist/templates/create",
    name: "live-assist-template-create",
    component: () => import("@/src/views/live_assist/TemplateCreateEdit.vue"),
    props: true,
  },
  {
    path: "/live-assist/templates/:id/edit",
    name: "live-assist-template-edit",
    component: () => import("@/src/views/live_assist/TemplateCreateEdit.vue"),
    props: true,
  },
  {
    path: "/live-assist/activity",
    name: "live-assist-activity",
    component: () => import("@/src/views/live_assist/Activity.vue"),
    props: true,
  },
  {
    path: "/live-assist/activity/:id",
    name: "live-assist-session-detail",
    component: () => import("@/src/views/live_assist/SessionDetail.vue"),
    props: true,
  },

  // ── Developer Center ──────────────────────────────────────────────
  {
    path: "/developer-center",
    redirect: "/developer-center/app-listings",
  },
  {
    path: "/developer-center/app-listings",
    name: "developer-center-listings",
    component: () => import("@/src/views/developer_center/AppListings.vue"),
  },
  {
    path: "/developer-center/publish",
    name: "developer-center-publish",
    component: () => import("@/src/views/developer_center/PublishApp.vue"),
  },
  {
    path: "/developer-center/submissions/:id",
    name: "developer-center-detail",
    component: () =>
      import("@/src/views/developer_center/AppSubmissionDetail.vue"),
    props: true,
  },

  // ── Ecom Marketing (Contlo iframe) ──────────────────────────────────
  {
    path: "/ecom-marketing",
    name: "ecom-marketing",
    component: () =>
      import("@/src/views/ecom_marketing/EcomMarketingIframe.vue"),
  },

  // ── Community Apps (iframe-embedded) ────────────────────────────────
  {
    path: "/community-app/:appCode",
    name: "community-app",
    component: () => import("@/src/views/community_app/CommunityAppIframe.vue"),
    props: true,
  },

  // ────────────────────── Developer Tools ──────────────────────
  {
    path: "/developer_tools/codebase",
    name: "codebase",
    component: () => import("@/src/views/codebase/repository/Repository.vue"),
    props: true,
  },
  {
    path: "/developer_tools/codebase/repository/:repo_id",
    name: "code-repository",
    component: () => import("@/src/views/codebase/repository/Repository.vue"),
    props: true,
  },
  {
    path: "/developer_tools/codebase/repository/:repo_id/commit/:commit_id",
    name: "commit-details",
    component: () => import("@/src/views/codebase/commits/CommitDetails.vue"),
    props: true,
  },
  {
    path: "/developer_tools/codebase/repository/:repo_id/pull/:number",
    name: "pull-request-detail",
    component: () =>
      import("@/src/views/codebase/pull_requests/PullRequestDetails.vue"),
    props: (route) => ({
      repoId: route.params.repo_id,
      number: route.params.number,
    }),
  },
  {
    path: "/developer_tools/codebase/repository/:repo_id/pull/new",
    name: "pull-request-create",
    component: () =>
      import("@/src/views/codebase/pull_requests/CreatePullRequest.vue"),
    props: true,
  },
  {
    path: "/developer_tools/codebase/repository/:repo_id/action/new",
    name: "action-create",
    component: () => import("@/src/views/codebase/actions/TemplatesView.vue"),
    props: true,
  },
  {
    path: "/developer_tools/codebase/repository/:repo_id/action/:action_id",
    name: "action-details",
    component: () => import("@/src/views/codebase/actions/ActionDetails.vue"),
    props: true,
  },
  {
    path: "/developer_tools/codebase/repository/:repo_id/branch/protection/create",
    name: "branch-protection-rule-create",
    component: () =>
      import("@/src/components/codebase_components/branch_protection/BranchProtectionRuleForm.vue"),
  },
  {
    path: "/developer_tools/codebase/repository/:repo_id/branch/protection/:rule_id/edit",
    name: "branch-protection-rule-edit",
    component: () =>
      import("@/src/components/codebase_components/branch_protection/BranchProtectionRuleForm.vue"),
  },
  {
    path: "/developer_tools/codebase/repository/:repo_id/rulesets/create",
    name: "ruleset-create",
    component: () => import("@/src/views/codebase/rulesets/RulesetForm.vue"),
  },
  {
    path: "/developer_tools/codebase/repository/:repo_id/rulesets/:ruleset_id/edit",
    name: "ruleset-edit",
    component: () => import("@/src/views/codebase/rulesets/RulesetForm.vue"),
  },
  // ────────────────────── Code Reviewer ──────────────────────
  {
    path: "/developer_tools/code_reviewer",
    name: "code-reviewer",
    component: () => import("@/src/views/code_reviewer/CodeReviewer.vue"),
    props: true,
  },
  // ── E-Sign ──────────────────────────────────────────────────────────
  {
    path: "/esign",
    redirect: "/esign/agreements",
  },
  {
    path: "/esign/agreements",
    name: "esign-agreements",
    component: () => import("@/src/views/esign/Agreements.vue"),
    props: true,
  },
  {
    path: "/esign/agreements/create",
    name: "esign-create-agreement",
    component: () => import("@/src/views/esign/CreateAgreement.vue"),
    props: true,
  },
  {
    path: "/esign/agreements/:id/edit",
    name: "esign-field-editor",
    component: () => import("@/src/views/esign/FieldEditor.vue"),
    meta: { hideSidebar: true, hideNavbar: true },
    props: true,
  },
  {
    path: "/esign/agreements/:id",
    name: "esign-agreement-detail",
    component: () => import("@/src/views/esign/AgreementDetail.vue"),
    meta: { hideContainerPadding: true },
    props: true,
  },
  {
    path: "/esign/templates",
    name: "esign-templates",
    component: () => import("@/src/views/esign/Templates.vue"),
    props: true,
  },
  {
    path: "/esign/templates/:id",
    name: "esign-template-detail",
    component: () => import("@/src/views/esign/TemplateDetail.vue"),
    meta: { hideContainerPadding: true },
    props: true,
  },
  {
    path: "/esign/templates/create",
    name: "esign-create-template",
    component: () => import("@/src/views/esign/CreateTemplate.vue"),
    props: true,
  },
  {
    path: "/esign/templates/:id/edit",
    name: "esign-edit-template",
    component: () => import("@/src/views/esign/CreateTemplate.vue"),
    props: true,
  },
  {
    path: "/esign/sign/:token",
    name: "esign-signing",
    component: () => import("@/src/views/esign/SigningView.vue"),
    meta: { public: true, hideSidebar: true, hideNavbar: true },
    props: true,
  },

  // ────────────────────── Sandbox ──────────────────────
  {
    path: "/sandbox",
    name: "sandbox-list",
    component: () => import("@/src/views/sandbox/SandboxList.vue"),
    props: true,
  },
  {
    path: "/sandbox/:id",
    name: "sandbox-detail",
    component: () => import("@/src/views/sandbox/SandboxDetail.vue"),
    props: true,
  },

  // ────────────────────── HR ──────────────────────
  {
    path: `/${apiVariants.PEOPLE}`,
    name: `${apiVariants.PEOPLE}`,
    children: [
      {
        path: "hidden/:slug",
        name: `${apiVariants.PEOPLE}-hidden-object`,
        component: () => import("@/src/views/dynamic_crm/DynamicCRMView.vue"),
        props: (route) => ({ slug: route.params.slug }),
        meta: { hideNavbar: true, hideSidebar: true },
      },
      {
        path: ":slug",
        name: `${apiVariants.PEOPLE}-objects`,
        component: () => import("@/src/views/dynamic_crm/DynamicCRMView.vue"),
        props: (route) => ({ slug: route.params.slug }),
      },
      {
        path: ":slug/:id",
        name: `${apiVariants.PEOPLE}-object-detail`,
        component: () =>
          import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
        props: (route) => ({
          id: route.params.id,
          slug: route.params.slug,
        }),
      },
    ],
  },
  {
    path: `/${apiVariants.TIME_OFF}`,
    name: `${apiVariants.TIME_OFF}`,
    children: [
      {
        path: "requests",
        name: `${apiVariants.TIME_OFF}-requests`,
        component: () =>
          import("@/src/views/human_resource/time_off/Requests.vue"),
        props: true,
      },
      {
        path: "approvals",
        name: `${apiVariants.TIME_OFF}-approvals`,
        component: () =>
          import("@/src/views/human_resource/time_off/Approvals.vue"),
        props: true,
      },
      {
        path: "leaves",
        name: `${apiVariants.TIME_OFF}-leaves`,
        component: () =>
          import("@/src/views/human_resource/time_off/Leaves.vue"),
        props: true,
      },
      {
        path: ":slug",
        name: `${apiVariants.TIME_OFF}-crm-objects`,
        component: () => import("@/src/views/dynamic_crm/DynamicCRMView.vue"),
        props: (route) => ({ slug: route.params.slug }),
      },
    ],
  },
  {
    path: `/${apiVariants.ATTENDANCE}`,
    name: `${apiVariants.ATTENDANCE}`,
    children: [
      {
        path: "check",
        name: `${apiVariants.ATTENDANCE}-section`,
        component: () =>
          import("@/src/views/human_resource/attendance/Attendance.vue"),
        props: true,
      },
      {
        path: "employees",
        name: `${apiVariants.ATTENDANCE}-employees`,
        component: () =>
          import("@/src/views/human_resource/attendance/Employees.vue"),
        props: true,
      },
      {
        path: ":slug",
        name: `${apiVariants.ATTENDANCE}-crm-objects`,
        component: () => import("@/src/views/dynamic_crm/DynamicCRMView.vue"),
        props: (route) => ({ slug: route.params.slug }),
      },
    ],
  },
  {
    path: `/${apiVariants.ATS}`,
    name: `${apiVariants.ATS}`,
    children: [
      {
        path: "candidates",
        name: `${apiVariants.ATS}-candidates`,
        component: () =>
          import("@/src/views/human_resource/ats/CandidateCRMView.vue"),
        props: () => ({ slug: "candidates" }),
      },
      {
        path: "candidates/:id",
        name: `${apiVariants.ATS}-candidate-detail`,
        component: () =>
          import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
        props: (route) => ({
          id: route.params.id,
          slug: "candidates",
        }),
      },
      {
        path: "campaigns",
        name: "ats-campaigns",
        component: () =>
          import("@/src/views/human_resource/ats/CampaignCRMView.vue"),
        props: () => ({ slug: "campaigns" }),
      },
      {
        path: "campaigns/:id/edit",
        name: "ats-campaign-edit",
        component: () =>
          import("@/src/views/human_resource/ats/ATSCampaignEdit.vue"),
        props: (route) => ({ id: route.params.id }),
      },
      {
        path: ":slug",
        name: `${apiVariants.ATS}-objects`,
        component: () => import("@/src/views/dynamic_crm/DynamicCRMView.vue"),
        props: (route) => ({ slug: route.params.slug }),
      },
      {
        path: ":slug/:id",
        name: `${apiVariants.ATS}-object-detail`,
        component: () =>
          import("@/src/views/dynamic_crm/detail_page/DynamicCRMDetail.vue"),
        props: (route) => ({
          id: route.params.id,
          slug: route.params.slug,
        }),
      },
    ],
  },
  {
    path: `/${apiVariants.PERFORM}`,
    name: `${apiVariants.PERFORM}`,
    children: [
      {
        path: "performance",
        name: `${apiVariants.PERFORM}-section`,
        component: () =>
          import("@/src/views/human_resource/perform/Performance.vue"),
        props: true,
      },
      {
        path: ":slug",
        name: `${apiVariants.PERFORM}-crm-objects`,
        component: () => import("@/src/views/dynamic_crm/DynamicCRMView.vue"),
        props: (route) => ({ slug: route.params.slug }),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory("/"),
  routes,
});

const preloadComponents = () => {
  if (preloadingDone) {
    return;
  }

  const componentsToPreload = [
    import("@/src/views/home/v1/Home.vue"),
    import("@/src/views/get_started/GetStarted.vue"),
    import("@/src/views/chats/AgiChat.vue"),
    import("@/src/views/refer/Refer.vue"),
    import("@/src/views/conversations/Conversations.vue"),
    import("@/src/views/unibox/Unibox.vue"),
    import("@/src/views/emails/Emails.vue"),
    import("@/src/views/campaign/Campaigns.vue"),
    import("@/src/views/sequence/Sequences.vue"),
    import("@/src/views/sequence/actions/SequenceEdit.vue"),
    import("@/src/views/sequence/details/SequenceStepDetail.vue"),
    import("@/src/views/settings/Settings.vue"),
    import("@/src/views/search/Search.vue"),
    import("@/src/views/app_store/AppStore.vue"),
    import("@/src/views/app_store/AppDetail.vue"),
    import("@/src/views/signals/Signals.vue"),
    import("@/src/views/signals/SignalSetup.vue"),
    import("@/src/views/prospect/ProspectCompanyView.vue"),
    import("@/src/views/prospect/FindCompanies.vue"),
    import("@/src/views/campaign/CampaignEdit.vue"),
    import("@/src/views/campaign/CampaignDetail.vue"),
    import("@/src/views/campaign/CampaignFilter.vue"),
    import("@/src/views/prospect/ProspectLeadView.vue"),
    import("@/src/views/prospect/FindLeads.vue"),
    import("@/src/views/integrations/ManageIntegrations.vue"),
    import("@/src/views/content/Content.vue"),
    import("@/src/views/content/ContentView.vue"),
    import("@/src/views/onboarding/Onboarding.vue"),
    import("@/src/views/prospect/Prospect.vue"),
    import("@/src/views/calls/call_campaigns/CallCampaigns.vue"),
    import("@/src/views/calls/call_logs/CallLogs.vue"),
    import("@/src/views/billing/setup/SetupBillingV2.vue"),
    import("@/src/views/customer_success/Summary.vue"),
    import("@/src/views/customer_success/Onboarding.vue"),
    import("@/src/views/customer_success/Customers.vue"),
    import("@/src/views/customer_success/Deals.vue"),
    import("@/src/views/customer_success/Tasks.vue"),
    import("@/src/views/customer_success/Alerts.vue"),
  ];

  Promise.all(componentsToPreload)
    .then(() => {
      preloadingDone = true;
      console.log("All components preloaded successfully");
    })
    .catch((error) => {
      console.error("Error preloading components:", error);
    });
};

let preloadingDone = false;

router.beforeEach(async (to, from, next) => {
  const isPublicRoute = to.meta.public === true;

  if (isPublicRoute) {
    if (to.meta.hideSidebar === undefined) {
      to.meta.hideSidebar = false;
    }
    if (to.meta.hideNavbar === undefined) {
      to.meta.hideNavbar = false;
    }
    next();
    return;
  }

  if (to.name && standardObjectRouteNames.includes(to.name)) {
    try {
      const shouldRedirect = await shouldRedirectToCrmRoute(to.name);
      if (shouldRedirect) {
        const crmPath = getCrmRoutePath(to.name);
        if (crmPath) {
          const query = to.query;
          const hash = to.hash;
          const fullPath = `${crmPath}${hash || ""}${Object.keys(query).length > 0 ? `?${new URLSearchParams(query).toString()}` : ""}`;
          next(fullPath);
          return;
        }
      }
    } catch (error) {
      console.error("Error checking route redirect:", error);
    }
  }

  const urlParams = new URLSearchParams(window.location.search);
  const workspaceId = urlParams.get("workspace_id");

  if (to.path.startsWith("/ai-journeys")) {
    const newPath = to.path.replace(
      "/ai-journeys",
      `/${apiVariants.SEQUENCES}`,
    );
    next(newPath);
    return;
  }

  if (to.path.startsWith("/ai-sdr")) {
    const newPath = to.path.replace("/ai-sdr", `/${apiVariants.OUTREACH}`);
    next(newPath);
    return;
  }

  if (to.path.startsWith("/agent_builder")) {
    const newPath = to.path.replace(
      "/agent_builder",
      `/${apiVariants.WORKFLOWS}`,
    );
    next(newPath);
    return;
  }

  if (to.path.startsWith("/inbound_agent/tickets")) {
    const newPath = to.path.replace("/inbound_agent/tickets", "/tickets");
    next(newPath);
    return;
  }

  await store.dispatch(
    "callsCampaign/setIsCampaignPageOpen",
    to.name === "sales-dialer-campaign-detail" ||
      to.name === "voice-agents-campaign-view",
  );

  if (workspaceId) {
    const currentWorkspaceId = localStorage.getItem("currentWorkspaceId");
    const isWorkspaceSwitch =
      currentWorkspaceId && currentWorkspaceId !== workspaceId;

    localStorage.setItem("currentWorkspaceId", workspaceId);
    document.cookie = `workspace_id=${workspaceId}; path=/; max-age=31536000`;

    if (isWorkspaceSwitch) {
      await store.dispatch("appStore/refreshAppStoreVersion");
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete("workspace_id");
      const queryString = urlParams.toString();
      const currentHash = window.location.hash || "";
      const cleanUrl = `${window.location.pathname}${queryString ? "?" + queryString : ""}${currentHash}`;
      window.history.replaceState({}, "", cleanUrl);
      next();
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete("workspace_id");
    const queryString = urlParams.toString();
    const currentHash = window.location.hash || "";
    window.location.replace(
      `${window.location.pathname}${queryString ? "?" + queryString : ""}${currentHash}`,
    );
    return;
  }

  if (to.name === "onboarding") {
    next();
    return;
  }

  try {
    const dataChecks = [
      {
        condition: () => store.state.onboarding.status === null,
        action: () => store.dispatch("onboarding/fetchStatus"),
      },
      {
        condition: () => store.state.getStarted.isCompleted === null,
        action: () => store.dispatch("getStarted/fetchStatus"),
      },
      {
        condition: () =>
          !store.getters["billing/isBillingSetupComplete"] &&
          store.state.billing.status === "",
        action: () => store.dispatch("billing/getBillingDetails"),
      },
    ];

    const promises = dataChecks
      .filter((check) => check.condition())
      .map((check) => check.action());

    if (promises.length > 0) {
      await Promise.all(promises);
    }

    if (store.state.onboarding.error) {
      next();
    } else if (!store.getters["onboarding/isOnboardingComplete"]) {
      next({ name: "onboarding" });
    } else {
      const isBillingSetupComplete =
        store.getters["billing/isBillingSetupComplete"];

      if (to.name === "setup-billing" && isBillingSetupComplete) {
        const appStoreVersion =
          window.APP_STORE_CONFIG?.version ||
          store.getters["appStore/appStoreVersion"];
        const redirectTo =
          appStoreVersion === "v2" ? "i-assistant" : "get_started";
        next({ name: redirectTo });
        return;
      }

      if (!isBillingSetupComplete && to.name !== "setup-billing") {
        next({
          name: "setup-billing",
          query: to.query,
        });
        return;
      }

      if (to.meta.hideSidebar === undefined) {
        to.meta.hideSidebar = false;
      }

      if (to.meta.hideNavbar === undefined) {
        to.meta.hideNavbar = false;
      }

      next();

      if (to.name === "home" && !preloadingDone) {
        setTimeout(preloadComponents, 1000);
      }
    }
  } catch (error) {
    console.error("Error in router guard:", error);
    next();
  }
});

router.afterEach((to) => {
  try {
    const pageName = getCleanPageName(to);

    MixpanelService.trackPageView(pageName, {
      Path: to.path,
      Timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("An error occurred in the router guard: ", error);
  }
});

router.onError((error, to) => {
  try {
    if (
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed")
    ) {
      console.log("New App deployed Reloading", error, to);
      if (!to?.fullPath) {
        window.location.reload();
      } else {
        window.location = to.fullPath;
      }
    }
  } catch (error) {
    console.error("An error occurred in the router guard: ", error);
  }
});

export default router;
